import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { ScrollBar, Input } from 'components/ui'
import PropTypes from 'prop-types'
import {
    SIDE_NAV_WIDTH,
    SIDE_NAV_COLLAPSED_WIDTH,
    NAV_MODE_DARK,
    NAV_MODE_THEMED,
    NAV_MODE_TRANSPARENT,
    SIDE_NAV_CONTENT_GUTTER,
    LOGO_X_GUTTER,
} from 'constants/theme.constant'
import Logo from 'components/template/Logo'
import navigationConfig from 'configs/navigation.config'
import pokemonNavConfig from 'configs/pokemonnav.config'
import VerticalMenuContent from 'components/template/VerticalMenuContent'
import useResponsive from 'utils/hooks/useResponsive'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next'

async function fetchPokemonData(locale) {
    try {
      const searchParameters = {
        language: locale
      };
      const queryParameters = new URLSearchParams(searchParameters).toString();
      const response = await fetch(`https://labmaus-website-b7a5f5f01a05.herokuapp.com/api/recent_top_usage?${queryParameters}`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      return data; // Assuming the API response is an array of Pokemon objects
    } catch (error) {
      console.error('Error fetching Pokemon data:', error);
      return [];
    }
}

const sideNavStyle = {
    width: SIDE_NAV_WIDTH,
    minWidth: SIDE_NAV_WIDTH,
}

const sideNavCollapseStyle = {
    width: SIDE_NAV_COLLAPSED_WIDTH,
    minWidth: SIDE_NAV_COLLAPSED_WIDTH,
}

function DebouncedInput({
    value: initialValue,
    onChange,
    debounce = 500,
    ...props
}) {
    const [value, setValue] = useState(initialValue)

    useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(value)
        }, debounce)

        return () => clearTimeout(timeout)
    }, [value, debounce, onChange])

    return (
        <div style={{display: "flex", padding: "1.0rem"}}>
            <div className="flex justify-end">
                <div className="flex items-center">
                    <Input
                        {...props}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                    />
                </div>
            </div>
        </div>
    )
}

const SideNav = () => {
    const locale = useSelector((state) => state.locale.currentLang)
    const [searchTerm, setSearchTerm] = useState('');
    const themeColor = useSelector((state) => state.theme.themeColor) 
    const primaryColorLevel = useSelector(
        (state) => state.theme.primaryColorLevel
    )
    const { t } = useTranslation();
    const navMode = useSelector((state) => state.theme.navMode)
    const mode = useSelector((state) => state.theme.mode)
    const direction = useSelector((state) => state.theme.direction)
    const currentRouteKey = useSelector(
        (state) => state.base.common.currentRouteKey
    )
    const sideNavCollapse = useSelector(
        (state) => state.theme.layout.sideNavCollapse
    )
    const userAuthority = useSelector((state) => state.auth.user.authority)

    const { larger } = useResponsive()

    const sideNavColor = () => {
        if (navMode === NAV_MODE_THEMED) {
            return `bg-${themeColor}-${primaryColorLevel} side-nav-${navMode}`
        }
        return `side-nav-${navMode}`
    }

    const logoMode = () => {
        if (navMode === NAV_MODE_THEMED) {
            return NAV_MODE_DARK
        }

        if (navMode === NAV_MODE_TRANSPARENT) {
            return mode
        }

        return navMode
    }

    const [usageData, setUsageData] = useState([]);
    const [loading, setLoading] = useState(true); // Add a loading state

    useEffect(() => {
    async function fetchAndUpdateConfig() {
        const pokemonData = await fetchPokemonData(locale)
            setUsageData(pokemonData);
            setLoading(false);
        }
        fetchAndUpdateConfig();
    }, [locale]);

    if (loading) {
        return <div>Loading...</div>
    } else {
        const navTree = navigationConfig
        const generalContent = (
            <VerticalMenuContent
                navMode={navMode}
                collapsed={sideNavCollapse}
                navigationTree={navTree}
                routeKey={currentRouteKey}
                userAuthority={userAuthority}
                direction={direction}
            />
        )
        const pokemonTree = pokemonNavConfig({pokemonData: usageData, searchTerm: searchTerm})
        const pokemonContent = (
            <VerticalMenuContent
                navMode={navMode}
                collapsed={sideNavCollapse}
                navigationTree={pokemonTree}
                routeKey={currentRouteKey}
                userAuthority={userAuthority}
                direction={direction}
            />
        )
        // Menu content for general, and a second one for pokemon, with search bar in the middle

        return (
            <>
                {larger.md && (
                    <div
                        style={
                            sideNavCollapse ? sideNavCollapseStyle : sideNavStyle
                        }
                        className={classNames(
                            'side-nav',
                            sideNavColor(),
                            !sideNavCollapse && 'side-nav-expand'
                        )}
                    >
                        <div className="side-nav-header">
                            <Link to="/home">
                                <Logo
                                    mode={logoMode()}
                                    type={sideNavCollapse ? 'streamline' : 'full'}
                                    gutter={
                                        sideNavCollapse
                                            ? SIDE_NAV_CONTENT_GUTTER
                                            : LOGO_X_GUTTER
                                    }
                                />
                            </Link>
                        </div>
                        {sideNavCollapse ? (
                            <div className="side-nav-content">
                                <ScrollBar autoHide direction={direction}>
                                {generalContent}
                                {pokemonContent}
                                </ScrollBar>
                            </div>
                        ) : (
                            <div className="side-nav-content">
                                <ScrollBar autoHide direction={direction}>
                                    {generalContent}
                                    <div>
                                        <DebouncedInput
                                            value={searchTerm ?? ''}
                                            onChange={(value) => setSearchTerm(String(value))}
                                            className="p-2 font-lg shadow border border-block"
                                            placeholder={t('nav.search')}
                                        />
                                    </div>
                                    {pokemonContent}
                                </ScrollBar>
                            </div>
                        )}
                    </div>
                )}
            </>
        )
    }
}

SideNav.propTypes = {
    themed: PropTypes.bool,
    darkMode: PropTypes.bool,
    themeColor: PropTypes.string,
}

SideNav.defaultProps = {
    themed: false,
    darkMode: false,
    themeColor: '',
}

export default SideNav
